import { defineComponent, ref, computed, watch, nextTick, onMounted, onBeforeUnmount, openBlock, createElementBlock, Fragment, renderList, normalizeClass, createBlock, TransitionGroup, withCtx, createVNode, createCommentVNode, normalizeStyle, h } from "vue";
import CheckTickCircleIcon from "../../assets/icons/check-tick-circle.svg.js";
import InfoIcon from "../../assets/icons/info.svg.js";
import Alert02StrokeRoundedIcon from "../../assets/icons/outline/alert-02-stroke-rounded.svg.js";
import BubbleChatQuestionStrokeRoundedIcon from "../../assets/icons/outline/bubble-chat-question-stroke-rounded.svg.js";
import InformationDiamondStrokeRoundedIcon from "../../assets/icons/outline/information-diamond-stroke-rounded.svg.js";
import UploadCircle01StrokeRoundedIcon from "../../assets/icons/outline/upload-circle-01-stroke-rounded.svg.js";
import { useNotificationStore } from "./notification.store.js";
import Notification from "./Notification.vue.js";
const _hoisted_1 = ["data-id", "data-position"];
const NOTIFICATION_STACK_LIMIT = 5;
const NOTIFICATION_STACK_GAP = 8;
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "NotificationManager",
  setup(__props) {
    const notificationStore = useNotificationStore();
    const { notifications } = notificationStore;
    const positions = ["bottom-right", "top-middle"];
    const notificationSlotHeight = ref({
      "bottom-right": 70,
      "top-middle": 70
    });
    const notificationHeights = ref({});
    const timers = /* @__PURE__ */ new Map();
    const statusIcons = {
      DANGER: () => h(Alert02StrokeRoundedIcon, { class: "text-red-800" }),
      WARNING: () => h(InfoIcon, { class: "text-yellow-800" }),
      SUCCESS: () => h(CheckTickCircleIcon, { class: "text-green-800" }),
      NEUTRAL: () => h(InformationDiamondStrokeRoundedIcon, { class: "text-grey-700" }),
      INFO: () => h(BubbleChatQuestionStrokeRoundedIcon, { class: "text-light-blue-800" }),
      UPDATE: () => h(UploadCircle01StrokeRoundedIcon, { class: "text-violet-800" }),
      DARK: () => h(InformationDiamondStrokeRoundedIcon, { class: "text-white" })
    };
    const autoDismissNotification = (notifId) => {
      const timer = setTimeout(() => {
        notificationStore.dismissNotification(notifId);
      }, 4e3);
      timers.set(notifId, timer);
    };
    const forceDismiss = (id) => {
      if (timers.has(id)) {
        clearTimeout(timers.get(id));
        timers.delete(id);
      }
      notificationStore.dismissNotification(id);
    };
    const syncNotificationSlotHeight = (el) => {
      const item = el;
      if (!item.classList.contains("notification-item--notification")) return;
      const pos = item.dataset.position;
      if (!pos) return;
      const height = item.getBoundingClientRect().height;
      const id = Number(item.dataset.id);
      if (Number.isFinite(id) && height > 0) {
        notificationHeights.value[id] = Math.round(height);
      }
      if (height > 0) {
        notificationSlotHeight.value[pos] = Math.max(notificationSlotHeight.value[pos], Math.round(height));
      }
    };
    const recalculateNotificationSlotHeights = () => {
      const stacks = document.querySelectorAll(".notification-stack[data-position]");
      stacks.forEach((stack) => {
        const pos = stack.dataset.position;
        if (!pos) return;
        const items = stack.querySelectorAll(".notification-item--notification");
        let maxHeight = 0;
        items.forEach((item) => {
          const h2 = item.getBoundingClientRect().height;
          const id = Number(item.dataset.id);
          if (Number.isFinite(id) && h2 > 0) {
            notificationHeights.value[id] = Math.round(h2);
          }
          if (h2 > maxHeight) maxHeight = h2;
        });
        if (maxHeight > 0) {
          notificationSlotHeight.value[pos] = Math.round(maxHeight);
        }
      });
    };
    const getNotificationStackStyle = (position) => ({
      "--notification-slot-height": `${notificationSlotHeight.value[position]}px`,
      "--notification-stack-gap": `${NOTIFICATION_STACK_GAP}px`,
      "--notification-limit": String(NOTIFICATION_STACK_LIMIT)
    });
    const getNotificationItemStyle = (position, items, index) => {
      let offset = NOTIFICATION_STACK_GAP;
      for (let i = 0; i < index; i += 1) {
        const prev = items[i];
        if (!prev) continue;
        const h2 = notificationHeights.value[prev.id] || notificationSlotHeight.value[position];
        offset += h2 + NOTIFICATION_STACK_GAP;
      }
      if (position === "bottom-right") {
        return {
          insetBlockEnd: `${offset}px`,
          insetInlineEnd: "0px"
        };
      }
      return {
        insetBlockStart: `${offset}px`,
        insetInlineStart: "0px",
        insetInlineEnd: "0px",
        marginInline: "auto"
      };
    };
    const getNotificationIcon = (notif) => {
      if (notif.icon) {
        return notif.icon;
      }
      const status = notificationStore.getNotificationStatus(notif.status);
      return status ? statusIcons[status] : void 0;
    };
    const notificationsByPosition = computed(() => {
      const grouped = {
        "bottom-right": { all: [], toastAlert: [], notification: [] },
        "top-middle": { all: [], toastAlert: [], notification: [] }
      };
      notifications.value.forEach((notif) => {
        const position = notif.position || "top-middle";
        grouped[position].all.push(notif);
        if (notif.type === "notification") {
          grouped[position].notification.push(notif);
        } else {
          grouped[position].toastAlert.push(notif);
        }
      });
      return grouped;
    });
    watch(
      notifications,
      (newNotifications) => {
        const activeIds = new Set(newNotifications.map((n) => n.id));
        timers.forEach((timer, id) => {
          if (!activeIds.has(id)) {
            clearTimeout(timer);
            timers.delete(id);
          }
        });
        newNotifications.forEach((notif) => {
          if (!notif.persist && !timers.has(notif.id)) {
            autoDismissNotification(notif.id);
          }
        });
        const activeNotificationIds = new Set(
          newNotifications.filter((n) => n.type === "notification").map((n) => n.id)
        );
        Object.keys(notificationHeights.value).forEach((idStr) => {
          const id = Number(idStr);
          if (!activeNotificationIds.has(id)) {
            delete notificationHeights.value[id];
          }
        });
        nextTick(() => {
          window.requestAnimationFrame(recalculateNotificationSlotHeights);
        });
      },
      { deep: true, immediate: true }
    );
    onMounted(() => {
      nextTick(() => {
        window.requestAnimationFrame(recalculateNotificationSlotHeights);
      });
    });
    onBeforeUnmount(() => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock(Fragment, null, renderList(positions, (position) => {
        return openBlock(), createElementBlock(Fragment, { key: position }, [
          notificationsByPosition.value[position].all.length > 0 ? (openBlock(), createElementBlock("div", {
            key: 0,
            class: normalizeClass(["push-notification-container", `push-notification-container--${position}`])
          }, [
            notificationsByPosition.value[position].toastAlert.length > 0 ? (openBlock(), createBlock(TransitionGroup, {
              key: 0,
              name: "toast-list",
              appear: ""
            }, {
              default: withCtx(() => [
                (openBlock(true), createElementBlock(Fragment, null, renderList(notificationsByPosition.value[position].toastAlert, (notif) => {
                  return openBlock(), createElementBlock("div", {
                    key: notif.id,
                    class: normalizeClass(["notification-item", `notification-item--${notif.type || "toast"}`])
                  }, [
                    createVNode(Notification, {
                      type: notif.type,
                      status: notif.status,
                      position: notif.position || "top-middle",
                      text: notif.text,
                      icon: getNotificationIcon(notif),
                      "additional-content": notif.additionalContent,
                      "show-close-icon": notif.showCloseIcon,
                      clickable: !!notif.onClick,
                      rounded: notif.rounded,
                      onClick: ($event) => notif.onClick && notif.onClick(),
                      onClose: () => {
                        notif.onClose?.();
                        forceDismiss(notif.id);
                      }
                    }, null, 8, ["type", "status", "position", "text", "icon", "additional-content", "show-close-icon", "clickable", "rounded", "onClick", "onClose"])
                  ], 2);
                }), 128))
              ]),
              _: 2
            }, 1024)) : createCommentVNode("", true),
            notificationsByPosition.value[position].notification.length > 0 ? (openBlock(), createBlock(TransitionGroup, {
              key: 1,
              name: "notification-list",
              tag: "div",
              class: "notification-stack",
              "data-position": position,
              style: normalizeStyle(getNotificationStackStyle(position)),
              appear: "",
              onBeforeEnter: syncNotificationSlotHeight
            }, {
              default: withCtx(() => [
                (openBlock(true), createElementBlock(Fragment, null, renderList(notificationsByPosition.value[position].notification, (notif, index) => {
                  return openBlock(), createElementBlock("div", {
                    key: notif.id,
                    class: "notification-item notification-item--notification",
                    "data-id": notif.id,
                    "data-position": position,
                    style: normalizeStyle(getNotificationItemStyle(position, notificationsByPosition.value[position].notification, index))
                  }, [
                    createVNode(Notification, {
                      type: notif.type,
                      status: notif.status,
                      position: notif.position || "top-middle",
                      text: notif.text,
                      icon: getNotificationIcon(notif),
                      "additional-content": notif.additionalContent,
                      "show-close-icon": notif.showCloseIcon,
                      clickable: !!notif.onClick,
                      rounded: notif.rounded,
                      onClick: ($event) => notif.onClick && notif.onClick(),
                      onClose: () => {
                        notif.onClose?.();
                        forceDismiss(notif.id);
                      }
                    }, null, 8, ["type", "status", "position", "text", "icon", "additional-content", "show-close-icon", "clickable", "rounded", "onClick", "onClose"])
                  ], 12, _hoisted_1);
                }), 128))
              ]),
              _: 2
            }, 1032, ["data-position", "style"])) : createCommentVNode("", true)
          ], 2)) : createCommentVNode("", true)
        ], 64);
      }), 64);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=NotificationManager.vue2.js.map
